import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { specialDayApi } from '../services/dashboardSpecialDayApi';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, PlusCircle, Edit, Trash, CheckCircle, Sparkles, Eye } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import InfoModal from '../components/common/InfoModal';
import CustomDatePicker from '../components/common/CustomDatePicker';
import { SpecialDayBannerCard, BANNER_VISUAL_MOTIFS, resolveBannerMotif } from '../components/dashboard/DashboardSpecialDayBanner';

const THEME_TYPES = {
    1: 'Resmi / Milli',
    2: 'Dini Bayram',
    3: 'Şirket İçi Özel Gün',
    4: 'Kutlama / Tebrik',
    5: 'Bilgilendirme / Duyuru'
};

const MOTIF_LABELS = {
    ataturk: 'Atatürk & Ay-Yıldız',
    flag: 'Türk Bayrağı',
    religious: 'Hilal & Kandil (Ramazan)',
    kurban: 'Kâbe & Hilal (Kurban)',
    celebration: 'Balonlar & Konfeti',
    newyear: 'Yılbaşı & Kar',
    labor: '1 Mayıs Dayanışma',
    company: 'Başarı Kupası',
    info: 'Duyuru Megafonu'
};

const parseLocalDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) {
        return new Date(val.getFullYear(), val.getMonth(), val.getDate());
    }
    const str = String(val).split('T')[0];
    const parts = str.split('-');
    if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    const d = new Date(val);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toLocalDateString = (date) => {
    const d = parseLocalDate(date);
    if (!d || isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toLocalISOString = (date) => {
    const str = toLocalDateString(date);
    return str ? `${str}T00:00:00` : null;
};

const DashboardSpecialDaysPage = () => {
    const { firmCode, isAuthenticated, permissions } = useAuth();

    const [specialDays, setSpecialDays] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        message: '',
        description: '',
        startDate: null,
        endDate: null,
        themeType: 3,
        iconKey: '',
        themeColor: '',
        isActive: true,
        showOnDashboard: true,
        isDismissible: true,
        priority: 5,
        isReligious: false,
        isCompanySpecific: true
    });

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [selectedIdForEdit, setSelectedIdForEdit] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);

    const canManage = useMemo(() => permissions.includes('special_days:manage'), [permissions]);

    const fetchSpecialDays = useCallback(async () => {
        if (!canManage) {
            setError('Özel gün ve temaları görüntüleme yetkiniz bulunmamaktadır.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const data = await specialDayApi.getAll();
            setSpecialDays(data || []);
        } catch (err) {
            setError(err.message || 'Özel gün temaları yüklenirken bir hata oluştu.');
            console.error('API Hatası:', err);
        } finally {
            setIsLoading(false);
        }
    }, [canManage]);

    useEffect(() => {
        if (!isAuthenticated || !firmCode) {
            setError('Giriş yapılmamış veya firma kodu mevcut değil.');
            setIsLoading(false);
            return;
        }
        if (!isAddModalOpen && !isEditModalOpen && !isDeleteConfirmModalOpen) {
            fetchSpecialDays();
        }
    }, [firmCode, isAuthenticated, isAddModalOpen, isEditModalOpen, isDeleteConfirmModalOpen, fetchSpecialDays]);

    const clearForm = () => {
        setFormData({
            title: '',
            message: '',
            description: '',
            startDate: null,
            endDate: null,
            themeType: 3,
            iconKey: '',
            themeColor: '',
            isActive: true,
            showOnDashboard: true,
            isDismissible: true,
            priority: 5,
            isReligious: false,
            isCompanySpecific: true
        });
        setError(null);
    };

    const handleAddClick = () => {
        clearForm();
        setMessage(null);
        setIsAddModalOpen(true);
    };

    const handleEditClick = (row) => {
        setFormData({
            title: row.title || '',
            message: row.message || '',
            description: row.description || '',
            startDate: parseLocalDate(row.startDate),
            endDate: parseLocalDate(row.endDate),
            themeType: row.themeType && row.themeType >= 3 ? row.themeType : 3,
            iconKey: row.iconKey || '',
            themeColor: row.themeColor === 'blue' ? '' : (row.themeColor || ''),
            isActive: row.isActive ?? true,
            showOnDashboard: row.showOnDashboard ?? true,
            isDismissible: row.isDismissible ?? true,
            priority: row.priority || 0,
            isReligious: row.isReligious || false,
            isCompanySpecific: row.isCompanySpecific ?? true
        });
        setSelectedIdForEdit(row.id);
        setIsEditModalOpen(true);
        setError(null);
        setMessage(null);
    };

    const handleDeleteClick = (id) => {
        setIdToDelete(id);
        setIsDeleteConfirmModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateForm = () => {
        if (!formData.title.trim()) return 'Başlık boş olamaz.';
        if (!formData.message.trim()) return 'Mesaj boş olamaz.';
        if (!formData.startDate) return 'Başlangıç tarihi seçmelisiniz.';
        if (!formData.endDate) return 'Bitiş tarihi seçmelisiniz.';
        if (formData.endDate < formData.startDate) return 'Bitiş tarihi başlangıç tarihinden önce olamaz.';
        return null;
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                startDate: toLocalISOString(formData.startDate),
                endDate: toLocalISOString(formData.endDate),
                themeType: parseInt(formData.themeType, 10),
                priority: parseInt(formData.priority, 10) || 0,
                isCompanySpecific: true
            };
            await specialDayApi.create(payload);
            setMessage('Özel gün / kutlama banner\'ı başarıyla oluşturuldu.');
            setIsAddModalOpen(false);
        } catch (err) {
            setError(err.message || 'Özel gün oluşturulurken bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                startDate: toLocalISOString(formData.startDate),
                endDate: toLocalISOString(formData.endDate),
                themeType: parseInt(formData.themeType, 10),
                priority: parseInt(formData.priority, 10) || 0,
                isCompanySpecific: true
            };
            await specialDayApi.update(selectedIdForEdit, payload);
            setMessage('Özel gün / kutlama banner\'ı başarıyla güncellendi.');
            setIsEditModalOpen(false);
        } catch (err) {
            setError(err.message || 'Özel gün güncellenirken bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await specialDayApi.delete(idToDelete);
            setMessage('Özel gün başarıyla silindi.');
            setIsDeleteConfirmModalOpen(false);
        } catch (err) {
            setError(err.message || 'Özel gün silinirken bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
            setIdToDelete(null);
        }
    };

    const handleToggleActive = async (id, currentStatus) => {
        try {
            await specialDayApi.toggleActive(id, !currentStatus);
            fetchSpecialDays();
        } catch (err) {
            setError(err.message || 'Durum güncellenirken hata oluştu.');
        }
    };

    const columns = useMemo(
        () => [
            {
                header: 'Durum',
                accessor: 'isActive',
                render: (row) => (
                    <button
                        onClick={() => handleToggleActive(row.id, row.isActive)}
                        className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer transition-colors ${
                            row.isActive
                                ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                    >
                        {row.isActive ? 'Aktif' : 'Pasif'}
                    </button>
                )
            },
            {
                header: 'Başlık & Mesaj',
                accessor: 'title',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{row.title}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{row.message}</span>
                    </div>
                )
            },
            {
                header: 'Tarih Aralığı',
                accessor: 'dateRange',
                render: (row) => {
                    const sDate = parseLocalDate(row.startDate);
                    const eDate = parseLocalDate(row.endDate);
                    const s = sDate ? sDate.toLocaleDateString('tr-TR') : '-';
                    const e = eDate ? eDate.toLocaleDateString('tr-TR') : '-';
                    return s === e ? s : `${s} - ${e}`;
                }
            },
            {
                header: 'Tema & Görsel Motif',
                accessor: 'themeType',
                render: (row) => {
                    const resolved = resolveBannerMotif(row);
                    return (
                        <div className="flex flex-col space-y-1">
                            <span className="px-2 py-0.5 w-fit inline-flex text-xs leading-5 font-medium rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                                {THEME_TYPES[row.themeType] || 'Diğer'}
                            </span>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center">
                                <Sparkles className="w-3 h-3 mr-1 text-amber-500" />
                                {MOTIF_LABELS[resolved] || 'Standart'}
                            </span>
                        </div>
                    );
                }
            },
            { header: 'Öncelik', accessor: 'priority', sortable: true },
            {
                header: 'Gösterim',
                accessor: 'display',
                render: (row) => (
                    <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400">
                        <span>Dashboard: {row.showOnDashboard ? 'Açık' : 'Kapalı'}</span>
                        <span>Kapatılabilir: {row.isDismissible ? 'Evet' : 'Hayır'}</span>
                    </div>
                )
            },
            {
                header: 'İşlemler',
                accessor: 'actions',
                render: (row) => (
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={() => handleEditClick(row)}
                            className="p-1.5 rounded-full text-indigo-500 hover:bg-indigo-50 dark:hover:bg-gray-700"
                            title="Düzenle"
                        >
                            <Edit className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => handleDeleteClick(row.id)}
                            className="p-1.5 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-gray-700"
                            title="Sil"
                        >
                            <Trash className="w-5 h-5" />
                        </button>
                    </div>
                )
            }
        ],
        []
    );

    if (!canManage && !isLoading) {
        return <div className="p-4 text-red-700">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır. (Gereken yetki: special_days:manage)</div>;
    }

    const renderFormFields = () => (
        <div className="space-y-4">
            {/* Canlı Banner Önizlemesi */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/50 p-3">
                <div className="flex items-center text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                    <Eye className="w-3.5 h-3.5 mr-1.5 text-indigo-500" />
                    Dashboard Canlı Banner Önizlemesi
                </div>
                <SpecialDayBannerCard banner={formData} isPreview={true} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlık *</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Örn: Şirketimizin 10. Kuruluş Yıl Dönümü Kutlu Olsun!"
                        required
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kısa Kutlama / Duyuru Mesajı *</label>
                    <textarea
                        name="message"
                        rows="2"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Dashboard'da görünecek ana tebrik veya bilgilendirme mesajı..."
                        required
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alt Açıklama / Detay (Opsiyonel)</label>
                    <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Örn: Saat 15:00'te konferans salonunda kutlama etkinliğimiz yapılacaktır."
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Tarihi *</label>
                    <CustomDatePicker
                        selectedDate={toLocalDateString(formData.startDate)}
                        onDateChange={(d) => {
                            const date = parseLocalDate(d);
                            setFormData((prev) => ({
                                ...prev,
                                startDate: date,
                                endDate: prev.endDate && date && date > prev.endDate ? date : prev.endDate || date
                            }));
                        }}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi *</label>
                    <CustomDatePicker
                        selectedDate={toLocalDateString(formData.endDate)}
                        minDate={formData.startDate}
                        onDateChange={(d) => {
                            setFormData((prev) => ({ ...prev, endDate: parseLocalDate(d) }));
                        }}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tema Kategorisi *</label>
                    <select
                        name="themeType"
                        value={formData.themeType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        <option value={3}>Şirket İçi Özel Gün (Mavi / Kurumsal)</option>
                        <option value={4}>Kutlama & Tebrik (Fuşya / Pembe + Konfeti)</option>
                        <option value={5}>Bilgilendirme & Duyuru (Slate / Cyan)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Görsel Motif / İllüstrasyon</label>
                    <select
                        name="iconKey"
                        value={formData.iconKey}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                        {BANNER_VISUAL_MOTIFS.map((m) => (
                            <option key={m.value} value={m.value}>
                                {m.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Öncelik (Priority)</label>
                    <input
                        type="number"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="0 (En yüksek olan öncelikli gösterilir)"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Özel Arka Plan Rengi / CSS (Opsiyonel)</label>
                    <input
                        type="text"
                        name="themeColor"
                        value={formData.themeColor}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Boş bırakılırsa temanın kendi gradyanı kullanılır"
                    />
                </div>
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t dark:border-gray-700">
                    <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} className="rounded text-blue-600" />
                        <span>Aktif</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" name="showOnDashboard" checked={formData.showOnDashboard} onChange={handleChange} className="rounded text-blue-600" />
                        <span>Dashboard'da Göster</span>
                    </label>
                    <label className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input type="checkbox" name="isDismissible" checked={formData.isDismissible} onChange={handleChange} className="rounded text-blue-600" />
                        <span>Kullanıcı Kapatabilsin</span>
                    </label>
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-1">
            {message && (
                <div className="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 p-3 rounded-md mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" /> {message}
                </div>
            )}
            {error && (
                <div className="bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 p-3 rounded-md mb-4 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" /> {error}
                </div>
            )}

            <div className="mb-4 bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 rounded-md">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Manuel Özel Banner & Kutlama Yönetimi</h3>
                        <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                            <p>
                                Bu sayfadan şirket içi özel günleri, kuruluş yıl dönümlerini, kutlamaları ve bilgilendirme banner'larını yönetebilirsiniz.
                                <br />
                                <strong>Not:</strong> Resmi tatiller (29 Ekim, 23 Nisan, 19 Mayıs, 30 Ağustos vb.) ve dini bayram banner'ları doğrudan <b>"Resmi Tatiller"</b> sayfasından tatil kaydına bağlı olarak yönetilir.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <DataTable
                data={specialDays}
                columns={columns}
                itemsPerPage={15}
                showSearch={true}
                showPagination={true}
                emptyMessage="Henüz manuel şirket içi özel gün veya kutlama banner'ı eklenmemiş."
                isLoading={isLoading}
                actionsHeader={
                    <button
                        onClick={handleAddClick}
                        className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors shadow-sm"
                    >
                        <PlusCircle className="w-5 h-5 mr-2" /> Özel Gün / Banner Ekle
                    </button>
                }
            />

            <InfoModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Yeni Manuel Özel Gün / Kutlama Banner'ı Ekle" showFooter={false} className="max-w-4xl">
                <form onSubmit={handleAddSubmit} className="space-y-4">
                    {renderFormFields()}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t dark:border-gray-700">
                        <button
                            type="button"
                            onClick={() => setIsAddModalOpen(false)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                            disabled={isSubmitting}
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                            disabled={isSubmitting}
                        >
                            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Kaydet
                        </button>
                    </div>
                </form>
            </InfoModal>

            <InfoModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Manuel Özel Gün / Kutlama Banner'ı Düzenle" showFooter={false} className="max-w-4xl">
                <form onSubmit={handleEditSubmit} className="space-y-4">
                    {renderFormFields()}
                    <div className="flex justify-end space-x-3 mt-6 pt-4 border-t dark:border-gray-700">
                        <button
                            type="button"
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                            disabled={isSubmitting}
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                            disabled={isSubmitting}
                        >
                            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Güncelle
                        </button>
                    </div>
                </form>
            </InfoModal>

            <InfoModal
                isOpen={isDeleteConfirmModalOpen}
                onClose={() => setIsDeleteConfirmModalOpen(false)}
                title="Silme Onayı"
                message="Bu özel gün / tema kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
                type="confirm"
                onConfirm={handleDeleteConfirm}
                confirmText="Evet, Sil"
                cancelText="İptal"
                isLoading={isSubmitting}
            />
        </div>
    );
};

export default DashboardSpecialDaysPage;
